import { isArray } from 'lodash-es';
import type { IntlShape } from 'react-intl';

import type {
  FilterSegmentVariableDefinition,
  ResultRecord,
  ResultRecordValue,
} from '@dynatrace-sdk/client-query';

import { SEGMENTSELECTOR_MESSAGES } from './segmentMessages.js';

/**
 * @internal
 */
export function convertQueryRecordToStoreSegmentVariables(
  records: (ResultRecord | null)[],
): FilterSegmentVariableDefinition[] {
  if (records.length === 0) {
    return [];
  }

  const allKeys = Object.keys(records[0] ?? {});

  return allKeys.map((key) => {
    const values = records.reduce<string[]>((values, singleRow) => {
      if (!singleRow || !(key in singleRow)) {
        values.push('null');
        return values;
      }

      if (typeof singleRow[key] === 'string') {
        values.push(singleRow[key]);
      }

      if (isArray(singleRow[key])) {
        values = values.concat(
          flattenQueryResultToValues(
            values,
            singleRow[key] as (ResultRecord | null)[],
          ),
        );
      }

      return Array.from(new Set(values));
    }, []);

    return { name: key, values };
  });
}

/**
 * We try to parse a string that might be JSON, if it fails we return the original data.
 * In case the parse fails due to a SyntaxError, we assume it's not JSON (standard JSON.parse error type).
 * If it fails for any other reason, we return 'null' to avoid breaking the flow.
 * @param data
 * @returns
 */
const parsePossibleJson = (data: unknown) => {
  try {
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch (error) {
    if (error instanceof SyntaxError) {
      return data;
    }
    return 'null';
  }
};

/**
 *
 * @param record - ResultRecordValue
 * @returns stringified version of the result
 * @internal
 */
export function flattenQueryResultToValues(
  base: string[],
  row: ResultRecordValue[],
): string[] {
  const flattenedValues = row.reduce<string[]>((acc, data) => {
    // When the values are retrieved from a preset, it might still be the former syntax where arrays are stringified.
    // So we try to parse it first.
    const parsedData = parsePossibleJson(data);

    if (parsedData === null) {
      acc.push('null');
      return acc;
    }

    if (
      typeof parsedData === 'string' ||
      typeof parsedData === 'number' ||
      typeof parsedData === 'boolean'
    ) {
      acc.push(parsedData.toString());
      return acc;
    }

    if (isArray(parsedData) && isArray(acc)) {
      acc = acc.concat(flattenQueryResultToValues(acc, parsedData));
      return acc;
    }

    // We shouldn't reach this point, but just in case
    acc.push(JSON.stringify(parsedData));
    return acc;
  }, base);

  return Array.from(new Set(flattenedValues));
}

/**
 * Helper function to return a readable textvalue for segment variable values
 * that can be rendered in triggers, options, tooltips and presets.
 * @internal
 */
export function getReadableValue(
  value: string | null | undefined,
  intl: IntlShape,
) {
  if (value === '') {
    return intl.formatMessage(SEGMENTSELECTOR_MESSAGES.emptyString);
  }

  return value;
}
